import { filter } from 'rxjs/operators';
import { map, Observable } from 'rxjs';
import { TuiSelectModule } from '@taiga-ui/kit';
import { TuiDataListModule, TuiLoaderModule, TuiTextfieldControllerModule } from '@taiga-ui/core';
import { createUserContextHeader, debugUserContextHeader, WO_CONTEXT_HEADER } from '@iss/libs/user-context';
import { CountriesGQL, Locale, Maybe, Role } from '@iss/libs/graphql';
import { DefaultContext } from '@apollo/client/core/types';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import {
  Component,
  inject,
  Inject,
  Input,
  OnChanges,
  Self,
  SimpleChanges,
} from '@angular/core';
import { AsyncPipe, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';

import { CountryFlagComponent } from '../country-flag';

import { FindPipe } from './find.pipe';
import { Countries } from './country-input.types';

import { IsFalsePipe } from '@iss/wo-common/pipes';
import { Control, NOOP_VALUE_ACCESSOR } from '@iss/core/forms';

@Component({
  selector: 'wo-country-input',
  templateUrl: './country-input.component.html',
  standalone: true,
  imports: [
    AsyncPipe,
    CountryFlagComponent,
    FindPipe,
    IsFalsePipe,
    NgForOf,
    NgIf,
    NgTemplateOutlet,
    ReactiveFormsModule,
    TuiDataListModule,
    TuiLoaderModule,
    TuiSelectModule,
    TuiTextfieldControllerModule,
  ],
})
export class CountryInputComponent implements OnChanges {
  private readonly query = inject(CountriesGQL);

  public items$!: Observable<Countries>;
  public loading$!: Observable<boolean>;

  public constructor(
    @Self() @Inject(NgControl) public readonly control: Control<FormControl<Locale | null>>,
  ) {
    this.control.valueAccessor = NOOP_VALUE_ACCESSOR;
  }

  @Input() public role: Maybe<Role>;
  @Input() public countries: Maybe<readonly Locale[]>;
  @Input() public readonly: Maybe<boolean>;

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.items$ || 'role' in changes) {
      this.initQuery();
    }
  }

  private initQuery(): void {
    const query$ = this.query.watch(undefined, {
      fetchPolicy: this.role ? 'no-cache' : 'no-cache',
      context: this.context,
    }).valueChanges;

    this.loading$ = query$.pipe(map(({ loading }) => loading));
    this.items$ = query$.pipe(
      filter(({ data }) => Boolean(data)),
      map(({ data }) => data.countries),
      map(countries => this.countries
        ? countries.filter(country => this.countries!.includes(country.code))
        : countries),
    );
  }

  private get context(): DefaultContext | undefined {
    if (!this.role) return;

    const userContextHeader = createUserContextHeader({ role: this.role });
    debugUserContextHeader('country-input', userContextHeader);

    return {
      headers: {
        [WO_CONTEXT_HEADER]: userContextHeader,
      },
    };
  }
}
